/**
 * Yardım.
 *
 * A topic list beside a content panel. All the words live in `helpContent`;
 * this file is navigation only, which is what keeps the product claims
 * readable - and testable - as one document rather than as prose scattered
 * through components.
 *
 * The selected topic is in the URL as `?konu=<id>`, so a topic can be linked
 * to and survives a reload. The ids are stable slugs, never DOM indices: a
 * link built from "the fourth item" breaks the day a topic is inserted above
 * it, and breaks silently by showing the wrong page. An unknown or missing
 * `konu` falls back to the first topic rather than rendering nothing.
 *
 * The topic filter is a plain substring match over titles and keywords, run in
 * memory over seventeen entries. There is no index, no fuzzy matching and no
 * full-text search of the bodies: this is a table of contents that got long
 * enough to want a filter, not a documentation platform.
 */

import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { IconSearch } from "@/components/icons";
import { PageHeader, Section } from "@/components/product";
import { HELP_TOPICS, searchTopics, type HelpTopic } from "./helpContent";
import "./support.css";

const TOPIC_PARAM = "konu";
const QUERY_PARAM = "ara";

export function HelpScreen() {
  const [params, setParams] = useSearchParams();

  const query = params.get(QUERY_PARAM) ?? "";
  const requested = params.get(TOPIC_PARAM);
  const topic: HelpTopic =
    HELP_TOPICS.find((item) => item.id === requested) ?? HELP_TOPICS[0]!;

  const matches = useMemo(() => searchTopics(query), [query]);

  /**
   * Both controls write to the same query string.
   *
   * `replace` so filtering the list does not fill the browser's back stack
   * with one entry per keystroke; choosing a topic is a normal navigation and
   * pushes.
   */
  const update = (next: Record<string, string>, replace: boolean) => {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === "") merged.delete(key);
      else merged.set(key, value);
    }
    setParams(merged, { replace });
  };

  return (
    <div className="stack">
      <PageHeader
        title="Yardım"
        subtitle="SentyCap'in nasıl çalıştığı, iyi bir çekimin nasıl yapıldığı ve neyin neden başarısız olduğu."
      />

      <div className="help">
        <nav className="help__nav" aria-label="Yardım konuları">
          <div className="help__search">
            <IconSearch size={16} />
            <input
              type="search"
              value={query}
              onChange={(event) => update({ [QUERY_PARAM]: event.target.value }, true)}
              placeholder="Konu ara…"
              aria-label="Yardım konusu ara"
            />
          </div>

          {matches.length === 0 ? (
            <p className="muted small help__no-match">
              Bu aramaya uyan konu yok.
            </p>
          ) : (
            <ul>
              {matches.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`help__topic${topic.id === item.id ? " help__topic--active" : ""}`}
                    aria-current={topic.id === item.id ? "page" : undefined}
                    onClick={() => update({ [TOPIC_PARAM]: item.id }, false)}
                  >
                    <item.icon size={18} />
                    <span>{item.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </nav>

        <div className="help__content">
          <Section title={topic.title}>{topic.body()}</Section>
        </div>
      </div>
    </div>
  );
}
